import { zodResolver } from "@hookform/resolvers/zod";
import { MessagesSquare, Upload, X, XOctagon } from "@tamagui/lucide-icons";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { useUpdateUserProfile } from "@/api/mutations/userProfile";
import { Footer, Logo, Sheet, XStack, YStack, useMedia } from "@/components";
import { Button } from "@/components/Button";
import { Input } from "@/components/forms/Input";
import { H4, Paragraph } from "@/components/Typography";

const emailSchema = z.object({
  email: z.email("Please enter a valid email address"),
});

type EmailSchemaType = z.infer<typeof emailSchema>;

interface RevokeConsentConfirmationProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  userEmail: string | null | undefined;
}

export default function RevokeConsentConfirmation({
  open,
  onClose,
  onConfirm,
  userEmail,
}: RevokeConsentConfirmationProps) {
  const { lg } = useMedia();
  const { mutate: updateUserProfile, isPending: isUpdatingEmail } =
    useUpdateUserProfile();

  const { control, handleSubmit, reset } = useForm<EmailSchemaType>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: userEmail || undefined },
  });

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const hasNoEmail = !userEmail;

  const handleConfirm = handleSubmit((formData) => {
    if (hasNoEmail && formData.email) {
      updateUserProfile(
        { data: formData },
        {
          onSuccess: () => {
            reset(formData);
            onConfirm();
          },
        },
      );
    } else {
      onConfirm();
    }
  });

  const content = (
    <YStack gap="$200">
      <H4>Are you sure you want to stop data sharing?</H4>
      <Paragraph color="$textSecondary">
        In order for this product to work and for Curai Health to effectively
        provide you health support, data sharing must be enabled.
      </Paragraph>
      <H4>What will happen if you stop sharing?</H4>
      <YStack gap="$150" marginBottom="$200">
        <XStack gap="$100" alignItems="flex-start">
          <XOctagon
            size={20}
            color="$iconBase"
            flexShrink={0}
            marginTop="$25"
          />
          <Paragraph size="$sm">
            You will not be able to use the Curai Health platform anymore
          </Paragraph>
        </XStack>
        <XStack gap="$100" alignItems="flex-start">
          <MessagesSquare
            size={20}
            color="$iconBase"
            flexShrink={0}
            marginTop="$25"
          />
          <Paragraph size="$sm">
            If you are waiting on any results or additional follow-up from your
            provider, they will have to reach out to you by calling or emailing
          </Paragraph>
        </XStack>
        <XStack gap="$100" alignItems="flex-start">
          <Upload size={20} color="$iconBase" flexShrink={0} marginTop="$25" />
          <Paragraph size="$sm">
            You can get a download of your records by emailing
            support@curaihealth.com
          </Paragraph>
        </XStack>
      </YStack>
      {hasNoEmail && (
        <Controller
          control={control}
          name="email"
          render={({
            field: { onChange, onBlur, value },
            fieldState: { error },
          }) => (
            <Input.Controlled
              label="Email for clinical follow-up"
              value={value === "" ? undefined : value}
              placeholder="email@example.com"
              errorText={error?.message}
              onChangeText={onChange}
              onBlur={onBlur}
            />
          )}
        />
      )}
    </YStack>
  );

  if (lg) {
    if (!open) return null;
    return (
      <>
        {content}
        <XStack justifyContent="flex-end">
          <Button
            mode="tertiary"
            loading={isUpdatingEmail}
            disabled={isUpdatingEmail}
            onPress={() => handleConfirm()}
          >
            I'm sure I want to stop sharing
          </Button>
          <Button mode="primary" onPress={onClose}>
            Continue data sharing
          </Button>
        </XStack>
      </>
    );
  }

  return (
    <Sheet
      modal
      open={open}
      onOpenChange={(isOpen: boolean) => !isOpen && onClose()}
      snapPoints={[98]}
      disableDrag
    >
      <Sheet.Overlay
        backgroundColor="$fillOverlay"
        backdropFilter="blur(8px)"
        transition="standard"
        enterStyle={{ opacity: 0 }}
        exitStyle={{ opacity: 0 }}
      />
      <Sheet.Frame padding="$100">
        <XStack justifyContent="flex-end">
          <Button.Wrapper
            round
            onPress={onClose}
            aria-label="Close"
            padding="$100"
            backgroundColor="$transparent"
          >
            <X size={24} />
          </Button.Wrapper>
        </XStack>
        <XStack justifyContent="center" marginBottom="$200">
          <Logo.Only size={28} />
        </XStack>
        <YStack justifyContent="space-between" flexGrow={1} padding="$100">
          {content}
          <Footer marginTop="$100">
            <Button mode="primary" onPress={onClose}>
              Continue data sharing
            </Button>
            <Button
              mode="tertiary"
              loading={isUpdatingEmail}
              disabled={isUpdatingEmail}
              onPress={() => handleConfirm()}
            >
              I'm sure I want to stop sharing
            </Button>
          </Footer>
        </YStack>
      </Sheet.Frame>
    </Sheet>
  );
}
